import { Theme } from "@mui/material";
import { round, truncate } from "lodash";

import { ABBREVIATED_OPTION, Config, HIDE_DECIMAL, NO_SYMBOL } from "@/hooks/useConfig";
import { MROIColumnSelector } from "@/pages/channel-roi/constants/types";
import { AggregatedData, SharedRevenueAggregatedData } from "@/pages/channel-roi/types";
import { BCPDataWithTotal } from "@/shared/types";
import { isAboveSampleSizeThreshold, isBelowSampleSizeThreshold } from "@/Utils/dataFilters";
import { Layer } from "@/Utils/hierarchy";

import {
  formatMarketingReturnAmountTooltip,
  formatMarketingReturnPercentageTooltip,
  formatROITooltip,
  formatSoeTooltip,
  formatSpendAndRevenueTooltip,
  renderDataTooltip,
  renderInsufficientDataTooltip,
} from "./formatTooltip";
import { getShareOfEffectTooltipText, isMarketingReturn, isShareOfEffect } from "./helpers";
import { LegendOptions } from "./hooks/useColumnChartLegends";

export const MARKETING_SPEND_COLOR = "#1F4690";
export const MARKETING_REVENUE_COLOR = "#9C27B0";
export const SHARE_OF_EFFECT_COLOR = "#009693";
export const ROI_COLOR = "#4CAF50";
export const MARKETING_RETURN_COLOR = "#009650";

const BAR_WIDTH = "20px";

const GRID_CONFIG = {
  grid: {
    containLabel: true,
    top: "3%",
    left: 0,
    right: "3%",
    bottom: "4%",
  },
};

const X_AXIS_LABEL_CONFIG = (theme: Theme) => ({
  axisLabel: {
    color: theme.palette.brandedTypography?.textColor,
    fontSize: 14,
    interval: 0,
    formatter: (value: string) => truncate(value, { length: 16 }),
  },
});

const X_AXIS_CONFIG = (theme: Theme, channels: string[]) => ({
  xAxis: {
    type: "category",
    data: channels,
    ...X_AXIS_LABEL_CONFIG(theme),
  },
});

const Y_AXIS_SPLIT_LINE_CONFIG = (theme: Theme) => ({
  splitLine: {
    show: true,
    lineStyle: {
      type: "dashed",
      color: theme.palette.ChartGuideLines.color,
    },
  },
});

const Y_AXIS_PCT_CONFIG = (theme: Theme) => ({
  yAxis: {
    type: "value",
    axisLabel: {
      formatter: (value: number) => `${round(value, 2)}%`,
      fontSize: 14,
      color: theme.palette.brandedTypography?.textColor,
    },
    ...Y_AXIS_SPLIT_LINE_CONFIG(theme),
  },
});

const Y_AXIS_AMOUNT_CONFIG = (theme: Theme, config: Config) => ({
  yAxis: {
    type: "value",
    axisLabel: {
      formatter: (value: number) =>
        config.currency.formatCurrency(
          value,
          config.showReturnInsteadOfRevenue
            ? { ...NO_SYMBOL, ...HIDE_DECIMAL }
            : { ...HIDE_DECIMAL, ...ABBREVIATED_OPTION }
        ),
      fontSize: 14,
      color: theme.palette.brandedTypography?.textColor,
    },
    ...Y_AXIS_SPLIT_LINE_CONFIG(theme),
  },
});

const TOOLTIP_CONFIG = (theme: Theme) => ({
  trigger: "axis",
  axisPointer: {
    type: "shadow",
  },
  backgroundColor: theme.palette.echartTooltip.background,
  textStyle: {
    color: theme.palette.echartTooltip.textColor,
  },
});

const LABEL_CONFIG = (theme: Theme, nodes: AggregatedData[] | SharedRevenueAggregatedData[]) => ({
  label: {
    show: true,
    position: "top",
    color: theme.palette.text.primary,
    offset: [60, 20],
    rotate: 90,
    formatter: (params: any) => {
      const item = nodes[params.dataIndex];
      const sampleSize = item?.sampleSize || 0;
      if (isBelowSampleSizeThreshold(sampleSize)) {
        return "Insufficient Sample Size";
      }
      return "";
    },
  },
});

const ROI_LABEL_CONFIG = (
  theme: Theme,
  nodes: AggregatedData[] | SharedRevenueAggregatedData[],
  position: "top" | "bottom"
) => {
  // Get all non-null ROI nodes
  const roiNodes = nodes.filter((node) => {
    if ("roi" in node) {
      return (node as AggregatedData).roi?.total != null;
    }
    return false;
  }) as AggregatedData[];

  // Check if all non-null ROI values are negative
  const allNegative = roiNodes.length > 0 && roiNodes.every((node) => node.roi.total < 0);

  return {
    label: {
      show: true,
      position: position,
      offset: allNegative ? [0, 10] : [0, -20],
      color: theme.palette.text.primary,
      formatter: (params: any) => {
        const item: any = nodes[params.dataIndex];

        if (item?.roi.total == null) {
          return "N/A";
        }
        const sampleSize = item?.sampleSize || 0;
        if (isBelowSampleSizeThreshold(sampleSize)) {
          return "Insufficient Sample Size";
        }
        return "";
      },
    },
  };
};

const SHARED_CONFIG = (theme: Theme, channels: string[]) => ({
  ...GRID_CONFIG,
  ...X_AXIS_CONFIG(theme, channels),
});

const getDataPoint = (sampleSize: number, value: number) =>
  isAboveSampleSizeThreshold(sampleSize) ? value : 0;

export const getROIChartOptions = (
  channels: string[],
  nodes: AggregatedData[],
  config: Config,
  theme: Theme,
  legendOptions: Pick<LegendOptions, "showROI">
) => ({
  ...SHARED_CONFIG(theme, channels),
  ...Y_AXIS_PCT_CONFIG(theme),
  tooltip: {
    ...TOOLTIP_CONFIG(theme),
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const channel = channels[dataIndex] || "";

      const isInsufficientData = isBelowSampleSizeThreshold(nodes?.[dataIndex]?.sampleSize ?? 0);
      if (isInsufficientData) {
        return renderInsufficientDataTooltip(channel, theme);
      }

      if (nodes?.[dataIndex]?.roi.total == null) {
        return renderDataTooltip(channel, theme, "Total ROI: N/A");
      }

      const roi = nodes?.[dataIndex]?.roi as BCPDataWithTotal;
      if (!roi) return "";

      const formatter = (value: number) => `${value}%`;
      return formatROITooltip(channel, roi, config, formatter, theme);
    },
  },
  series: [
    legendOptions.showROI && {
      name: "ROI (Positive)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(node.sampleSize, node.roi.total);
        return value >= 0 ? value : 0;
      }),
      color: theme.palette.channelEffectivenessChart?.roi,
      ...ROI_LABEL_CONFIG(theme, nodes, "top"),
      stack: "ROI",
      z: 1,
    },
    legendOptions.showROI && {
      name: "ROI (Negative)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [0, 0, 5, 5],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(node.sampleSize, node.roi.total);
        return value < 0 ? value : 0;
      }),
      color: theme.palette.channelEffectivenessChart?.roi,
      ...ROI_LABEL_CONFIG(theme, nodes, "bottom"),
      stack: "ROI",
      z: 1,
    },
  ],
});

export const getSpendRevenueChartOptions = (
  channels: string[],
  nodes: AggregatedData[],
  config: Config,
  theme: Theme,
  legendOptions: Pick<LegendOptions, "showMarketingSpend" | "showMarketingRevenue">,
  isPercentage: boolean = false
) => ({
  ...SHARED_CONFIG(theme, channels),
  ...(isPercentage ? Y_AXIS_PCT_CONFIG(theme) : Y_AXIS_AMOUNT_CONFIG(theme, config)),
  tooltip: {
    ...TOOLTIP_CONFIG(theme),
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const channel = channels[dataIndex] || "";

      const isInsufficientData = isBelowSampleSizeThreshold(nodes?.[dataIndex]?.sampleSize ?? 0);
      if (isInsufficientData) {
        return renderInsufficientDataTooltip(channel, theme);
      }

      const marketingSpend = (
        isPercentage ? nodes?.[dataIndex]?.spendPercentage : nodes?.[dataIndex]?.spend
      ) as BCPDataWithTotal;
      const marketingRevenue = (
        isPercentage ? nodes?.[dataIndex]?.revenuePercentage : nodes?.[dataIndex]?.revenue
      ) as BCPDataWithTotal;

      const marketingYield = nodes?.[dataIndex]?.yield as BCPDataWithTotal;

      if (!marketingSpend || !marketingRevenue) return "";

      const currencySymbol = config.currency.getSymbol();
      const yieldFormatter = (value: number) => `${currencySymbol}${round(value, 2)}`;

      const metrics = {
        spend: marketingSpend,
        revenue: marketingRevenue,
        yield: marketingYield,
      };

      const spendFormatter = (value: number) =>
        isPercentage
          ? `${round(value, 2)}%`
          : config.currency.formatCurrency(value, { ...ABBREVIATED_OPTION, ...HIDE_DECIMAL });

      const revenueFormatter = isPercentage
        ? (value: number) => `${round(value, 2)}%`
        : (value: number) =>
            config.currency.formatCurrency(
              value,
              config.showReturnInsteadOfRevenue
                ? { ...NO_SYMBOL, ...HIDE_DECIMAL, ...ABBREVIATED_OPTION }
                : { ...HIDE_DECIMAL, ...ABBREVIATED_OPTION }
            );

      return formatSpendAndRevenueTooltip(
        channel,
        metrics,
        legendOptions,
        config,
        spendFormatter,
        revenueFormatter,
        yieldFormatter,
        theme,
        isPercentage
      );
    },
  },
  series: [
    legendOptions.showMarketingSpend && {
      name: "Marketing Spend (Positive)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.spendPercentage.total : node.spend.total
        );
        return value >= 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingSpend,
      ...LABEL_CONFIG(theme, nodes),
      stack: "Spend",
      z: 1,
    },
    legendOptions.showMarketingSpend && {
      name: "Marketing Spend (Negative)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [0, 0, 5, 5],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.spendPercentage.total : node.spend.total
        );
        return value < 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingSpend,
      stack: "Spend",
      z: 1,
    },
    legendOptions.showMarketingRevenue && {
      name: "Marketing Revenue (Positive)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value >= 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingRevenue,
      stack: "Revenue",
      z: 1,
    },
    legendOptions.showMarketingRevenue && {
      name: "Marketing Revenue (Negative)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [0, 0, 5, 5],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value < 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingRevenue,
      stack: "Revenue",
      z: 1,
    },
  ],
});

export const getShareOfEffectChartOptions = (
  channels: string[],
  nodes: SharedRevenueAggregatedData[],
  config: Config,
  theme: Theme,
  legendOptions: Pick<LegendOptions, "showContribution">,
  isPercentage: boolean = false,
  funnelStage: string
) => ({
  ...SHARED_CONFIG(theme, channels),
  ...(isPercentage ? Y_AXIS_PCT_CONFIG(theme) : Y_AXIS_AMOUNT_CONFIG(theme, config)),
  tooltip: {
    ...TOOLTIP_CONFIG(theme),
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const channel = channels[dataIndex] || "";

      const isInsufficientData = isBelowSampleSizeThreshold(nodes?.[dataIndex]?.sampleSize ?? 0);
      if (isInsufficientData) {
        return renderInsufficientDataTooltip(channel, theme);
      }

      const data = isPercentage
        ? (nodes?.[dataIndex]?.revenuePercentage as BCPDataWithTotal)
        : (nodes?.[dataIndex]?.revenue as BCPDataWithTotal);

      if (!data) return "";

      const tooltipTitle = `Contribution ${isPercentage ? "%" : config.currency.getSymbol()}`;
      const funnelStageLabel = config.getLayerLabel(funnelStage as Layer).toLowerCase();
      const disclaimerText = getShareOfEffectTooltipText(funnelStageLabel, isPercentage);

      const formatter = (value: number) =>
        isPercentage
          ? `${round(value, 2)}%`
          : config.currency.formatCurrency(value, { abbreviate: false });

      return formatSoeTooltip(channel, data, formatter, theme, tooltipTitle, disclaimerText);
    },
  },
  series: [
    legendOptions.showContribution && {
      name: "Marketing Revenue (Positive)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value >= 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.shareOfEffect,
      ...LABEL_CONFIG(theme, nodes),
      stack: "ShareOfEffect",
      z: 1,
    },
    legendOptions.showContribution && {
      name: "Marketing Revenue (Negative)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [0, 0, 5, 5],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value < 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.shareOfEffect,
      stack: "ShareOfEffect",
      z: 1,
    },
  ],
});

export const getMarketingReturnChartOptions = (
  channels: string[],
  nodes: SharedRevenueAggregatedData[],
  config: Config,
  theme: Theme,
  legendOptions: Pick<LegendOptions, "showMarketingReturn">,
  isPercentage: boolean = false
) => ({
  ...SHARED_CONFIG(theme, channels),
  ...(isPercentage ? Y_AXIS_PCT_CONFIG(theme) : Y_AXIS_AMOUNT_CONFIG(theme, config)),
  tooltip: {
    ...TOOLTIP_CONFIG(theme),
    formatter: (params: any) => {
      const dataIndex = params[0].dataIndex;
      const channel = channels[dataIndex] || "";

      const isInsufficientData = isBelowSampleSizeThreshold(nodes?.[dataIndex]?.sampleSize ?? 0);
      if (isInsufficientData) {
        return renderInsufficientDataTooltip(channel, theme);
      }

      const data = isPercentage
        ? (nodes?.[dataIndex]?.revenuePercentage as BCPDataWithTotal)
        : (nodes?.[dataIndex]?.revenue as BCPDataWithTotal);

      if (!data) return "";

      const formatter = isPercentage
        ? (value: number) => `${round(value, 2)}%`
        : (value: number) =>
            config.currency.formatCurrency(
              value,
              config.showReturnInsteadOfRevenue ? NO_SYMBOL : {}
            );

      return isPercentage
        ? formatMarketingReturnPercentageTooltip(channel, data, config, formatter, theme)
        : formatMarketingReturnAmountTooltip(channel, data, config, formatter, theme);
    },
  },
  series: [
    legendOptions.showMarketingReturn && {
      name: "Marketing Revenue (Positive)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [5, 5, 0, 0],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value >= 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingReturn,
      ...LABEL_CONFIG(theme, nodes),
      stack: "MarketingReturn",
      z: 1,
    },
    legendOptions.showMarketingReturn && {
      name: "Marketing Revenue (Negative)",
      type: "bar",
      barWidth: BAR_WIDTH,
      itemStyle: {
        borderRadius: [0, 0, 5, 5],
      },
      emphasis: {
        focus: "none",
        itemStyle: {
          shadowBlur: 10,
          shadowOffsetX: 0,
          shadowColor: "rgba(0, 0, 0, 0.5)",
        },
      },
      data: nodes.map((node) => {
        const value = getDataPoint(
          node.sampleSize,
          isPercentage ? node.revenuePercentage.total : node.revenue.total
        );
        return value < 0 ? value : "-";
      }),
      color: theme.palette.channelEffectivenessChart?.marketingReturn,
      stack: "MarketingReturn",
      z: 1,
    },
  ],
});

export const getChartOptions = (
  columnView: MROIColumnSelector,
  nodes: SharedRevenueAggregatedData[] | AggregatedData[],
  config: Config,
  theme: Theme,
  legendOptions: LegendOptions,
  funnelStage: string
) => {
  const channels = nodes.map((node) => node.channel);

  if (isShareOfEffect(columnView) || isMarketingReturn(columnView)) {
    const sharedNodes = nodes as SharedRevenueAggregatedData[];
    const isPercentage =
      columnView === MROIColumnSelector.SHARE_OF_EFFECT_PERCENTAGE ||
      columnView === MROIColumnSelector.MARKETING_RETURN_PERCENTAGE;

    if (isShareOfEffect(columnView)) {
      return getShareOfEffectChartOptions(
        channels,
        sharedNodes,
        config,
        theme,
        legendOptions,
        isPercentage,
        funnelStage
      );
    }

    return getMarketingReturnChartOptions(
      channels,
      sharedNodes,
      config,
      theme,
      legendOptions,
      isPercentage
    );
  }

  const aggregatedNodes = nodes as AggregatedData[];
  const isPercentage = columnView === MROIColumnSelector.SPEND_VS_REVENUE_PERCENTAGE;

  if (columnView === MROIColumnSelector.ROI_PERCENTAGE) {
    // Sort ROI nodes with nulls at the end
    const sortedNodes = [...aggregatedNodes].sort((a, b) => {
      const aValue = a.roi?.total;
      const bValue = b.roi?.total;

      // Push nulls to end
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      // Normal numeric comparison
      return bValue - aValue; // Descending order
    });

    const sortedChannels = sortedNodes.map((node) => node.channel);

    return getROIChartOptions(sortedChannels, sortedNodes, config, theme, legendOptions);
  }

  return getSpendRevenueChartOptions(
    channels,
    aggregatedNodes,
    config,
    theme,
    legendOptions,
    isPercentage
  );
};
